import PhoneCarousel from "@/components/about/PhoneCarousel";
import {
  SectionDeck,
  DeckSection,
  Reveal,
  BackToTop,
  DesignTuner,
} from "@/components/deck";
import styles from "./proof.module.css";

/* Copy, screenshots and wordmark are taken from proofdelivery.app, which
   stays the app's own home; this page is Proof's place on theoddstack.com. */
const PROOF_SITE = "https://proofdelivery.app";
const APP_STORE_URL = "https://apps.apple.com/us/app/proof-photo-delivery/id6761442785";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=com.bpapp.app&hl=en_US";

const SCREENS = [
  {
    src: "/images/proof/dashboard.webp",
    caption: "Your day, in one screen",
    alt: "The photographer's dashboard: the next shoot with its date, time and location marked confirmed, counts of upcoming shoots, galleries still to deliver and unpaid invoices, then a list of things needing attention.",
  },
  {
    src: "/images/proof/bookings.webp",
    caption: "Bookings, confirmed",
    alt: "The bookings screen with tabs for upcoming, calendar and list views. Up next is a portrait session at a named location with the client's arrival time and a confirmed marker.",
  },
  {
    src: "/images/proof/client-detail.webp",
    caption: "Release, payment, access code",
    alt: "One client's page, showing their collections, photos and upcoming shoots alongside the amount receivable, a PayPal button, a signed model release, their client access code and a delivered collection.",
  },
  {
    src: "/images/proof/delivered-gallery.webp",
    caption: "Deliver the gallery",
    alt: "A collection marked delivered, headed with its name, photo count, client and date, with buttons to add photos or resync from storage, and a grid of the photographs themselves.",
  },
  {
    src: "/images/proof/client-collections.webp",
    caption: "Every photographer, one library",
    alt: "The client's own library: two collections totalling sixteen photos, with a tab for each of the two photographers who have delivered to them and the newest collection marked new.",
  },
];

export default function ProofPage() {
  return (
    <SectionDeck>
      <div className={styles.page}>
        {/* ═══════════ 0 — HERO ═══════════ */}
        <DeckSection index={0}>
          <div className={styles.heroLayout}>
            <div className={styles.heroCopy}>
              <Reveal dir="top">
                <img
                  className={styles.wordmark}
                  src="/images/proof/proof-wordmark.svg"
                  alt="Proof"
                />
              </Reveal>
              <Reveal dir="left" delay={0.08}>
                <h1 className={styles.bigTitle}>Your photos never expire.</h1>
              </Reveal>
              <Reveal dir="bottom" delay={0.16}>
                <p className={styles.lede}>
                  Deliver every shoot from your own Dropbox or Google Drive.
                  Ninety-nine cents, once.
                </p>
              </Reveal>
              <Reveal dir="bottom" delay={0.24}>
                <div className={styles.storeRow}>
                  <a className={styles.storeButton} href={APP_STORE_URL}>
                    <span className={styles.storeSub}>Download on the</span>
                    <span className={styles.storeMain}>App Store</span>
                  </a>
                  <a className={styles.storeButton} href={GOOGLE_PLAY_URL}>
                    <span className={styles.storeSub}>Get it on</span>
                    <span className={styles.storeMain}>Google Play</span>
                  </a>
                </div>
              </Reveal>
            </div>
            <Reveal dir="right" delay={0.12}>
              <div className={styles.heroPhoneSlot}>
                <PhoneCarousel
                  accentColor="#14B8A6"
                  images={SCREENS.map(({ src, caption }) => ({ src, alt: `Proof — ${caption}` }))}
                />
              </div>
            </Reveal>
          </div>
        </DeckSection>

        {/* ═══════════ 1 — WHY PROOF ═══════════ */}
        <DeckSection index={1}>
          <Reveal dir="top">
            <span className={styles.eyebrow}>Why Proof</span>
          </Reveal>
          <div className={styles.cardGrid}>
            <Reveal dir="left" delay={0.08}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Never expires.</span>
                <p className={styles.cardBody}>
                  Galleries don&rsquo;t vanish after 30 days. Photos live on
                  your client&rsquo;s device, not on a countdown.
                </p>
              </div>
            </Reveal>
            <Reveal dir="bottom" delay={0.14}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Your own storage.</span>
                <p className={styles.cardBody}>
                  Deliver from the Dropbox or Google Drive you already pay
                  for. No second storage bill.
                </p>
              </div>
            </Reveal>
            <Reveal dir="right" delay={0.2}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Ninety-nine cents.</span>
                <p className={styles.cardBody}>
                  Once. Not a month, not a year. Once.
                </p>
              </div>
            </Reveal>
          </div>
        </DeckSection>

        {/* ═══════════ 2 — IT ISN'T ONLY GALLERIES ═══════════ */}
        <DeckSection index={2}>
          <Reveal dir="top">
            <h2 className={styles.midTitle}>It isn&rsquo;t only galleries</h2>
          </Reveal>
          <Reveal dir="bottom" delay={0.08}>
            <p className={styles.lede}>
              Proof covers part of what a client-management subscription
              charges you for &mdash; HoneyBook starts at $348 a year, and
              doesn&rsquo;t deliver galleries at all.
            </p>
          </Reveal>
          <div className={`${styles.cardGrid} ${styles.cardGridFour}`}>
            <Reveal dir="scale" delay={0.12}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Bookings</span>
                <p className={styles.cardBody}>
                  Packages, line items, and a deposit. Confirmed shoots on a
                  calendar.
                </p>
              </div>
            </Reveal>
            <Reveal dir="scale" delay={0.16}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Invoices and deposits</span>
                <p className={styles.cardBody}>
                  Professional invoices, PayPal deposits, and a running view of
                  what&rsquo;s outstanding and what&rsquo;s collected.
                </p>
              </div>
            </Reveal>
            <Reveal dir="scale" delay={0.2}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Signed releases</span>
                <p className={styles.cardBody}>
                  Your own release wording. The model reads it and signs on
                  their own device.
                </p>
              </div>
            </Reveal>
            <Reveal dir="scale" delay={0.24}>
              <div className={styles.card}>
                <span className={styles.cardTitle}>Your clients</span>
                <p className={styles.cardBody}>
                  Import from your contacts. Every shoot, invoice, and gallery
                  against the right name.
                </p>
              </div>
            </Reveal>
          </div>
        </DeckSection>

        {/* ═══════════ 3 — SEE IT WORKING ═══════════ */}
        <DeckSection index={3}>
          <Reveal dir="top">
            <h2 className={styles.midTitle}>See it working</h2>
          </Reveal>
          <Reveal dir="bottom" delay={0.08}>
            <p className={styles.lede}>Screens from the app as it ships today.</p>
          </Reveal>
          <div className={styles.galleryGrid}>
            {SCREENS.map((s, i) => (
              <Reveal key={s.src} dir="scale" delay={0.04 * (i + 1)}>
                <figure className={styles.galleryItem}>
                  <img className={styles.galleryShot} loading="lazy" src={s.src} alt={s.alt} />
                  <figcaption className={styles.galleryCaption}>{s.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </DeckSection>

        {/* ═══════════ 4 — WHO IT'S FOR + BACK TO TOP ═══════════ */}
        <DeckSection index={4}>
          <Reveal dir="top">
            <h2 className={styles.midTitle}>Who it&rsquo;s for</h2>
          </Reveal>
          <div className={styles.cardGrid}>
            <Reveal dir="left" delay={0.08}>
              <a className={styles.card} href={`${PROOF_SITE}/photographers`}>
                <span className={styles.cardLabel}>Photographers</span>
                <p className={styles.cardBody}>
                  Deliver galleries, book sessions, send invoices, and get
                  releases signed.
                </p>
                <span className={styles.cardArrow}>Learn more &rarr;</span>
              </a>
            </Reveal>
            <Reveal dir="bottom" delay={0.14}>
              <a className={styles.card} href={`${PROOF_SITE}/models`}>
                <span className={styles.cardLabel}>Models</span>
                <p className={styles.cardBody}>
                  Every shoot from every photographer, in one place, on your
                  phone.
                </p>
                <span className={styles.cardArrow}>Learn more &rarr;</span>
              </a>
            </Reveal>
            <Reveal dir="right" delay={0.2}>
              <a className={styles.card} href={`${PROOF_SITE}/network`}>
                <span className={styles.cardLabel}>Proof Network</span>
                <p className={styles.cardBody}>
                  Find photographers and models around you. Launching city by
                  city.
                </p>
                <span className={styles.cardArrow}>Learn more &rarr;</span>
              </a>
            </Reveal>
          </div>
          <Reveal dir="bottom" delay={0.26}>
            <div className={styles.closingLinks}>
              <a href={PROOF_SITE}>proofdelivery.app &rarr;</a>
              <a href={`${PROOF_SITE}/support`}>Support</a>
              <a href={`${PROOF_SITE}/privacy`}>Privacy</a>
              <a href={`${PROOF_SITE}/terms`}>Terms</a>
            </div>
          </Reveal>
          <BackToTop delay={0.3} />
        </DeckSection>
      </div>

      <DesignTuner />
    </SectionDeck>
  );
}
